import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import './services.scss';
import Seo, { SITE_URL } from '../components/seo/Seo';
import { getServiceContent, groupedServices, servicePath } from '../content/services';
import { servicesRoot } from '../lib/i18n-routes';
import { CONTACT } from '../lib/contact';

function Services() {
    const { t, i18n } = useTranslation();
    const lang = i18n.language && i18n.language.startsWith('ru') ? 'ru' : 'uz';

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const groups = groupedServices();
    const total = groups.reduce((n, g) => n + g.items.length, 0);
    const path = servicesRoot(lang);
    const altPath = servicesRoot(lang === 'ru' ? 'uz' : 'ru');

    const schema = {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: t('services-page-title'),
        url: `${SITE_URL}${path}`,
        mainEntity: {
            '@type': 'ItemList',
            numberOfItems: total,
            itemListElement: groups.flatMap((g) => g.items).map((s, i) => {
                const c = getServiceContent(s.slug, lang);
                return {
                    '@type': 'ListItem',
                    position: i + 1,
                    name: c?.hero?.h1 || s.slug,
                    url: `${SITE_URL}${servicePath(s, lang)}`,
                };
            }),
        },
    };

    return (
        <main className="services-index">
            <Seo
                title={t('services-meta-title')}
                description={t('services-meta-desc')}
                lang={lang}
                path={path}
                altPath={altPath}
                schema={schema}
            />

            <section className="svc-hero svc-hero--index">
                <div className="container">
                    <nav className="svc-crumbs" aria-label="breadcrumb">
                        <Link to="/">Innobek</Link>
                        <span aria-hidden="true">/</span>
                        <span>{t('services')}</span>
                    </nav>

                    <span className="brand-asterisk svc-hero__mark">&#10033;</span>
                    <p className="h8 sub-title">{t('services')}</p>
                    <h1 className="svc-hero__title">{t('services-page-title')}</h1>
                    <p className="svc-hero__lead">{t('services-page-lead')}</p>

                    <div className="svc-hero__btns">
                        <a href={CONTACT.phoneHref} className="tf-button btn-effect">
                            <span className="effect">{t('free-advice')}</span>
                        </a>
                        <Link to="/#portfolio" className="tf-button btn-effect ghost">
                            <span className="effect">{t('portfolio')} &#8599;</span>
                        </Link>
                    </div>
                </div>
            </section>

            {groups.map((group) => (
                <section className="svc-section svc-group" key={group.key}>
                    <div className="container">
                        <h2 className="svc-h2">{t(group.i18n)}</h2>
                        <div className="svc-group__grid">
                            {group.items.map((s) => {
                                const c = getServiceContent(s.slug, lang);
                                if (!c) return null;
                                return (
                                    <Link className="svc-related__card" key={s.slug} to={servicePath(s, lang)}>
                                        <h3>{c.hero.h1}</h3>
                                        <p>{c.definition?.text || c.meta.description}</p>
                                        <span className="svc-related__more">{t('read-more')} &#8599;</span>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                </section>
            ))}

            {!total ? (
                <section className="svc-section">
                    <div className="container">
                        <p>{t('services-empty')}</p>
                    </div>
                </section>
            ) : null}
        </main>
    );
}

export default Services;
